import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';

import ConfirmModal from '@/components/ConfirmationModal/ConfirmModal';
import { formatDateYyyyMmDd } from '@/common/utils/format-date';
import { createAgentAssociationMigrationRest, fetchAgentsRest } from '@/services/agents/agents-service';

const NO_AGENT_VALUE = '__NO_AGENT__';

type SelectOption = { value: string; label: string };

const AgentAssociationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  sourceAgent: { id: string; alias: string; email: string } | null;
  selectedUserIds: string[];
  onSuccess: () => void;
}> = ({ isOpen, onClose, sourceAgent, selectedUserIds, onSuccess }) => {
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(null);
  const [destinationSearch, setDestinationSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedOption(null);
    setDestinationSearch('');
    setIsSubmitting(false);
    setShowConfirm(false);
  }, [isOpen]);

  const { data, isLoading } = useQuery({
    queryKey: ['agentAssociationMigrationDestinations', destinationSearch],
    queryFn: () =>
      fetchAgentsRest({
        skip: 0,
        take: 500,
        filter: destinationSearch ? { search: destinationSearch } : {},
      }),
    staleTime: 30_000,
  });

  const destinationAgentId = selectedOption?.value ?? null;

  const destinationOptions = useMemo(() => {
    const agentOptions: SelectOption[] =
      data?.agents
        .filter((a) => sourceAgent === null || a.id !== sourceAgent.id)
        .map((a) => ({
          value: a.id,
          label: `${a.alias} (${a.email})`,
        })) ?? [];

    // Only show "No Agent" option when migrating from an agent
    const options: SelectOption[] = sourceAgent
      ? [{ value: NO_AGENT_VALUE, label: 'No Agent' }, ...agentOptions]
      : agentOptions;

    // Always include the selected option so it stays visible after search clears
    if (selectedOption && selectedOption.value !== NO_AGENT_VALUE) {
      const alreadyIncluded = options.some((o) => o.value === selectedOption.value);
      if (!alreadyIncluded) {
        options.push(selectedOption);
      }
    }

    return options;
  }, [data?.agents, sourceAgent, selectedOption]);

  const destinationLabel = selectedOption?.label ?? '';

  const isNoAgent = destinationAgentId === NO_AGENT_VALUE;

  const isSameAsSource = sourceAgent && destinationAgentId === sourceAgent.id;
  const canConfirm = Boolean(destinationAgentId) && selectedUserIds.length > 0 && !isSubmitting && !isSameAsSource;

  const confirmMessage = isNoAgent
    ? 'Are you sure you want to remove the agent association from the selected users?'
    : `Are you sure you want to migrate the selected users to ${destinationLabel}?`;

  const handleSubmit = async () => {
    if (!destinationAgentId) return;
    setIsSubmitting(true);
    try {
      const result = await createAgentAssociationMigrationRest({
        sourceAgentId: sourceAgent?.id ?? null,
        destinationAgentId: isNoAgent ? null : destinationAgentId,
        userIds: selectedUserIds,
      });

      const scheduledDate = formatDateYyyyMmDd(result.effectiveFrom);
      if (isNoAgent) {
        toast.success(`Removal scheduled for ${scheduledDate}`);
      } else {
        toast.success(`Migration to ${destinationLabel} scheduled for ${scheduledDate}`);
      }
      onSuccess();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Unable to queue migration');
    } finally {
      setIsSubmitting(false);
      setShowConfirm(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
        <ModalHeader toggle={onClose}>Agent Association</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <div className="text-muted">Source</div>
            <div>
              {sourceAgent ? (
                <>
                  <strong>{sourceAgent.alias}</strong> <span className="text-muted">({sourceAgent.email})</span>
                </>
              ) : (
                <strong>No Agent</strong>
              )}
            </div>
          </div>

          <div className="mb-3">
            <div className="text-muted">Selected users</div>
            <div>
              <strong>{selectedUserIds.length}</strong>
            </div>
          </div>

          <FormGroup>
            <Label>Destination</Label>
            <Select
              isLoading={isLoading}
              options={destinationOptions}
              value={selectedOption}
              onChange={(option) => setSelectedOption(option)}
              onInputChange={(value, { action }) => {
                if (action === 'input-change') {
                  setDestinationSearch(value);
                }
              }}
              placeholder="Select destination agent"
            />
          </FormGroup>

          {isSameAsSource && <Alert color="danger">Destination agent cannot be the same as source agent</Alert>}
        </ModalBody>
        <ModalFooter className="gap-2">
          <Button outline color="dark" disabled={isSubmitting} onClick={onClose}>
            Cancel
          </Button>
          <Button color="primary" disabled={!canConfirm} onClick={() => setShowConfirm(true)}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>

      {showConfirm && destinationAgentId ? (
        <ConfirmModal
          title="Are you sure?"
          message={confirmMessage}
          confirmButtonText={isNoAgent ? 'Remove' : 'Migrate'}
          onClose={() => setShowConfirm(false)}
          onConfirm={handleSubmit}
        />
      ) : null}
    </>
  );
};

export default AgentAssociationModal;
